import { useEffect, useState } from "react";
import { Field } from "../modelDialogs/BaseDialog";
import type KnxProductData from "../KnxProductdata";
import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import { itx, noItx } from "../translationItx/itx";

export type TechnicalManual = {
  url: string;
  title?: string;
  documentType?: string;
};

type TechnicalManualProduct = {
  catalogId?: string;
  manufacturer?: string;
  orderNumber?: string;
  urls?: TechnicalManual[];
};

type TechnicalManualCatalogue = {
  products?: TechnicalManualProduct[];
};

export type TechnicalManualLinkProps = {
  productData?: KnxProductData;
  orderInformation?: { articleNo?: string };
};

let cataloguePromise: Promise<TechnicalManualCatalogue> | undefined;
let catalogueIndexPromise:
  | Promise<{
      byCatalogId: Map<string, TechnicalManualProduct>;
      byProductKey: Map<string, TechnicalManualProduct>;
    }>
  | undefined;
const manualCountCache = new Map<string, number>();

function normalizeText(value: unknown): string {
  return String(value ?? "")
    .normalize("NFKC") // no itx
    .toLowerCase()
    .replace(/[\s._-]+/g, "");
}

function normalizeCatalogId(value: unknown): string {
  return String(value ?? "")
    .trim()
    .replace(/^config-(?:2-)?/i, "")
    .toLowerCase();
}

function getCatalogueUrl(): string {
  const configuredBaseUrl = typeof state !== "undefined" && state.hostBaseUrl ? state.hostBaseUrl : "/";
  return `${configuredBaseUrl.replace(/\/?$/, "/")}data/technical-manual-urls.beta.json`;
}

function loadCatalogue(): Promise<TechnicalManualCatalogue> {
  if (!cataloguePromise) {
    cataloguePromise = fetch(getCatalogueUrl())
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Technical manual catalogue request failed: ${response.status}`);
        }
        return (await response.json()) as TechnicalManualCatalogue;
      })
      .catch((error) => {
        cataloguePromise = undefined;
        throw error;
      });
  }

  return cataloguePromise;
}

function loadCatalogueIndex() {
  if (!catalogueIndexPromise) {
    catalogueIndexPromise = loadCatalogue()
      .then((catalogue) => {
        const byCatalogId = new Map<string, TechnicalManualProduct>();
        const byProductKey = new Map<string, TechnicalManualProduct>();

        for (const product of Array.isArray(catalogue.products) ? catalogue.products : []) {
          if (!Array.isArray(product.urls) || product.urls.length === 0) continue;

          const catalogId = normalizeCatalogId(product.catalogId);
          if (catalogId) byCatalogId.set(catalogId, product);

          const productKey = `${normalizeText(product.manufacturer)}\u0000${normalizeText(product.orderNumber)}`;
          if (productKey !== "\u0000") byProductKey.set(productKey, product);
        }

        return { byCatalogId, byProductKey };
      })
      .catch((error) => {
        catalogueIndexPromise = undefined;
        throw error;
      });
  }

  return catalogueIndexPromise;
}

export async function findTechnicalManuals(
  catalogId: string,
  manufacturer: string,
  orderNumber: string,
): Promise<TechnicalManual[]> {
  const catalogueIndex = await loadCatalogueIndex();
  const normalizedCatalogId = normalizeCatalogId(catalogId);
  const normalizedProductKey = `${normalizeText(manufacturer)}\u0000${normalizeText(orderNumber)}`;

  const matchingProduct =
    (normalizedCatalogId ? catalogueIndex.byCatalogId.get(normalizedCatalogId) : undefined) ||
    (normalizedProductKey !== "\u0000" ? catalogueIndex.byProductKey.get(normalizedProductKey) : undefined);

  const manuals = (matchingProduct?.urls || []).filter(
    (manual) => typeof manual?.url === "string" && /^https?:\/\//i.test(manual.url),
  );
  if (normalizedCatalogId) manualCountCache.set(normalizedCatalogId, manuals.length);

  return manuals;
}

export function getCachedTechnicalManualCount(catalogId: string): number | undefined {
  return manualCountCache.get(normalizeCatalogId(catalogId));
}

function getManualLinkText(manuals: TechnicalManual[], index: number): string {
  return manuals.length > 1
    ? itx("technicalManual.viewManualNumbered", "Technical Manual {T1}", { T1: String(index + 1) })
    : itx("technicalManual.viewManual", "Technical Manual");
}

function getManualLabel(manual: TechnicalManual, index: number): string {
  const title = manual.title?.trim();
  if (title && title.length <= 140 && !/[<>]/.test(title) && !/\b(?:alt|src|class)\s*=/.test(title)) {
    return title;
  }
  return index === 0
    ? itx("technicalManual.open", "Open technical manual")
    : itx("technicalManual.openAdditional", "Open technical manual {T1}", { T1: String(index + 1) });
}

export type TechnicalManualCellProps = {
  catalogId?: string;
  manufacturer?: string;
  orderNumber?: string;
};

export function TechnicalManualCell({ catalogId = "", manufacturer = "", orderNumber = "" }: TechnicalManualCellProps) {
  const [manuals, setManuals] = useState<TechnicalManual[]>([]);

  useEffect(() => {
    let cancelled = false;

    if (!catalogId && (!manufacturer || !orderNumber)) {
      setManuals([]);
      return () => {
        cancelled = true;
      };
    }

    setManuals([]);
    findTechnicalManuals(catalogId, manufacturer, orderNumber)
      .then((nextManuals) => {
        if (!cancelled) setManuals(nextManuals);
      })
      .catch(() => {
        if (!cancelled) setManuals([]);
      });

    return () => {
      cancelled = true;
    };
  }, [catalogId, manufacturer, orderNumber]);

  if (manuals.length === 0) return <span>-</span>;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "3px 8px",
        height: "100%",
        minWidth: 0,
      }}
    >
      {manuals.map((manual, index) => (
        <button
          key={`${manual.url}-${index}`}
          type="button"
          title={getManualLabel(manual, index)}
          style={{
            appearance: "none",
            margin: 0,
            padding: "2px 5px",
            height: "22px",
            border: "1px solid #c9c9c9",
            borderRadius: "4px",
            boxShadow: "none",
            background: "#fff",
            color: "#007bff",
            fontSize: "11px",
            lineHeight: "16px",
            whiteSpace: "nowrap",
            cursor: "pointer",
          }}
          onClick={(event) => {
            event.stopPropagation();
            window.open(manual.url, "_blank", "noopener,noreferrer"); // no itx
          }}
        >
          {noItx("Manual")}
          <i className="las la-external-link-alt" style={{ marginLeft: "4px" }} />
        </button>
      ))}
    </div>
  );
}

export default function TechnicalManualLink(props: TechnicalManualLinkProps) {
  const { productData, orderInformation } = props;
  const productInfo = productData?.productInfo;
  const catalogId = productInfo?.CatalogId || productData?.modelDataId || "";
  const manufacturer = productInfo?.ManufacturerName || "";
  const orderNumber = orderInformation?.articleNo || productInfo?.OrderNumber || "";
  const [manuals, setManuals] = useState<TechnicalManual[]>([]);

  useOnChangeWatcher(productData);

  useEffect(() => {
    let cancelled = false;

    if (!catalogId && (!manufacturer || !orderNumber)) {
      setManuals([]);
      return () => {
        cancelled = true;
      };
    }

    setManuals([]);
    findTechnicalManuals(catalogId, manufacturer, orderNumber)
      .then((nextManuals) => {
        if (!cancelled) setManuals(nextManuals);
      })
      .catch(() => {
        if (!cancelled) setManuals([]);
      });

    return () => {
      cancelled = true;
    };
  }, [catalogId, manufacturer, orderNumber]);

  if (manuals.length === 0) return null;

  return (
    <>
      {manuals.map((manual, index) => (
        <Field key={`${manual.url}-${index}`} label={index === 0 ? itx("technicalManual.title", "PDF") : ""}>
          <a
            href={manual.url}
            target="_blank"
            rel="noopener noreferrer" // no itx
            style={{ color: "#007bff", textDecoration: "underline", overflowWrap: "anywhere" }}
            title={getManualLabel(manual, index)}
          >
            {getManualLinkText(manuals, index)}
            <i className="las la-external-link-alt" style={{ marginLeft: "5px" }} />
          </a>
        </Field>
      ))}
    </>
  );
}
